import { emit } from '../postMessage';
import { goToLobby, useOperatorLinks } from '../operatorLinks';

/**
 * End-of-session screen. Two variants:
 *   - platform (default): "return to your platform" + Return button that
 *     re-emits `session_expired` to the parent window. When the casino gave
 *     us a `lobby_url` the button becomes "Back to lobby": it still emits
 *     `session_expired` (so a listening parent can react) and then leaves
 *     for the lobby, so the player is never stuck on a dead screen.
 *   - embed (`onSignIn` provided): there is no platform to return to — the
 *     player signs back in through the auth modal instead. No postMessage.
 *   - demo link (`reason="demo-link"`): someone followed a `/demo/:id` link
 *     that lost its `?embed_key=` (messengers love truncating URLs). They
 *     never had a session, so "your session ended" is simply wrong — say
 *     what actually happened and let them ask for the full link.
 */
export function ExpiredScreen({
  onSignIn,
  reason = 'session',
}: {
  onSignIn?: () => void;
  reason?: 'session' | 'demo-link';
}) {
  const { lobbyUrl } = useOperatorLinks();
  const onReturn = () => {
    emit('session_expired');
    if (lobbyUrl) goToLobby(lobbyUrl);
  };
  if (reason === 'demo-link') {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="text-xl font-semibold">This demo link is incomplete</h1>
        <p className="text-sm opacity-70">
          Ask whoever shared it for the full link — it needs the part after
          the pack number.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-xl font-semibold">Session expired</h1>
      {onSignIn ? (
        <>
          <p className="text-sm opacity-70">Your session ended.</p>
          <button
            className="rounded bg-gp-button px-4 py-2 text-gp-bg"
            onClick={onSignIn}
            type="button"
          >
            Sign in
          </button>
        </>
      ) : (
        <>
          <p className="text-sm opacity-70">Please return to your platform and start a new session.</p>
          <button
            className="rounded bg-gp-button px-4 py-2 text-gp-bg"
            onClick={onReturn}
            type="button"
          >
            {lobbyUrl ? 'Back to lobby' : 'Return'}
          </button>
        </>
      )}
    </div>
  );
}
